import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Gauge, SkipForward, Sun, Moon, SlidersHorizontal, History, EyeOff } from "lucide-react";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { EVENTS, TIMELINE, PRESETS, formatClock } from "../../lib/eclipse";

export default function TemporalScrubberDock({
  timeMin, onTime, playing, onPlay, speed, onSpeed,
  danjon, onDanjon, bortle, onBortle, presetId, onPreset, coverage = 0, onHideHud,
}) {
  const [adv, setAdv] = useState(false);
  return (
    <motion.div
      data-testid="temporal-scrubber-dock"
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.25 }}
      className="dock-panel"
    >
      {/* Row 1: transport + clock */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <button data-testid="hide-hud-toggle" onClick={onHideHud} className="dock-icon-btn" title="Hide interface">
          <EyeOff size={16} />
        </button>
        <button data-testid="play-toggle" onClick={onPlay} className="dock-icon-btn">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button data-testid="speed-toggle" onClick={onSpeed} className="dock-icon-btn gap-1 px-3 w-auto">
          <Gauge size={14} /> <span className="font-mono text-xs">{speed}×</span>
        </button>
        <button data-testid="jump-totality" onClick={() => onTime(TIMELINE.max)} className="dock-icon-btn gap-1 px-3 w-auto">
          <SkipForward size={14} /><span className="text-xs hidden sm:inline">Totality</span>
        </button>
        <button
          data-testid="advanced-metrics-toggle"
          onClick={() => setAdv((v) => !v)}
          className={`dock-icon-btn gap-1 px-3 w-auto ${adv ? "text-orange-300 ring-1 ring-orange-400/40" : ""}`}
        >
          <SlidersHorizontal size={14} /><span className="text-xs inline">Advanced</span>
        </button>

        <Select value={presetId} onValueChange={onPreset}>
          <SelectTrigger data-testid="historical-eclipse-select" className="dock-select">
            <History size={13} className="opacity-70 shrink-0" />
            <SelectValue placeholder="Historical Eclipse" />
          </SelectTrigger>
          <SelectContent position="popper" side="top" sideOffset={8} className="max-h-[320px] bg-[#0a0f19] border-white/10 text-slate-200">
            {PRESETS.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`preset-${p.id}`} className="text-xs">
                {p.date} · {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto text-right">
          <div className="font-mono text-xl sm:text-2xl text-white leading-none" data-testid="clock-display">{formatClock(timeMin)}</div>
          <div className="telem-label">EDT · Aug 28</div>
        </div>
      </div>

      {/* Row 2: temporal scrubber with event markers */}
      <div className="relative pt-1 pb-9">
        <Slider
          data-testid="temporal-slider"
          value={[timeMin]}
          min={TIMELINE.start}
          max={TIMELINE.end}
          step={0.25}
          onPointerDown={() => onTime(timeMin)}
          onValueChange={(v) => onTime(v[0])}
          className="scrubber"
        />
        <div className="absolute left-0 right-0 top-6 h-0 pointer-events-none">
          {EVENTS.map((e) => (
            <div key={e.short} className="event-tick" style={{ left: `${(e.min / TIMELINE.end) * 100}%` }}>
              <span className="event-dot" />
              <span className="event-label">{e.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Row 3: Advanced Metrics — Orbital Position + Danjon + Bortle (collapsible) */}
      <AnimatePresence initial={false}>
        {adv && (
          <motion.div
            data-testid="advanced-metrics-panel"
            key="adv-panel"
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 12 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.32, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {/* Orbital Position / Manual Alignment */}
            <div className="pt-3 border-t border-white/5">
              <div className="flex justify-between flex-wrap gap-x-3 mb-1.5">
                <span className="telem-label">Orbital Position · Manual Alignment</span>
                <span className="font-mono text-xs text-orange-300 shrink-0">{(coverage * 100).toFixed(0)}% aligned</span>
              </div>
              <Slider
                data-testid="manual-alignment-slider"
                value={[timeMin]} min={TIMELINE.start} max={TIMELINE.end} step={0.25}
                onPointerDown={() => onTime(timeMin)}
                onValueChange={(v) => onTime(v[0])}
                className="scrubber align"
              />
              <div className="flex justify-between telem-label mt-1 opacity-60">
                <span>Moon exits shadow</span><span>Perfect Syzygy</span><span>Moon exits shadow</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-5 pt-4 mt-3 border-t border-white/5">
              <div className="flex-1 min-w-[220px]">
                <div className="flex justify-between mb-1.5">
                  <span className="telem-label">Danjon Aerosol Scale</span>
                  <span className="font-mono text-xs text-orange-300">L = {danjon.toFixed(1)}</span>
                </div>
                <Slider
                  data-testid="danjon-slider"
                  value={[danjon]} min={0} max={4} step={0.1}
                  onValueChange={(v) => onDanjon(v[0])}
                  className="scrubber danjon"
                />
                <div className="flex justify-between telem-label mt-1 opacity-60">
                  <span>L0 · Black Void</span><span>L4 · Bright Copper</span>
                </div>
              </div>

              <div className="flex items-center gap-3" data-testid="bortle-toggle-wrap">
                <div className="text-right">
                  <div className="telem-label">Sky Environment</div>
                  <div className="font-cinzel text-xs text-slate-300 tracking-wider">{bortle <= 3 ? "Rural Dark Sky · B1" : "City Lights · B8"}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Moon size={14} className={bortle <= 3 ? "text-sky-300" : "text-slate-600"} />
                  <Switch data-testid="bortle-toggle" checked={bortle > 3} onCheckedChange={(c) => onBortle(c ? 8 : 1)} />
                  <Sun size={14} className={bortle > 3 ? "text-amber-300" : "text-slate-600"} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
